import React, { useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useInsert } from '../../hooks/useInsert';
import { StaticPage } from '../editor/ObjectRenderer';
import { listBuildingBlocks } from '../../lib/projectApi';
import { blocksFor, blockContext, buildBlockObject, MONTH_NAMES, USER_CATEGORY } from '../../lib/buildingBlocks';
import { getCalendarPrefs, setCalendarPrefs } from '../../lib/calendarPrefs';
import { MenuItemRow } from './RibbonPrimitives';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, LayoutGrid, BookmarkPlus, Puzzle, Loader2 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Preview tile                                                         */
/* ------------------------------------------------------------------ */

/** Renders a block-local group object scaled to fit a tile (transparent background). */
export const BlockPreview = ({ object, width = 112, height = 76, className = '' }) => {
  const k = Math.min(width / Math.max(1, object.width), height / Math.max(1, object.height));
  const w = object.width * k;
  const h = object.height * k;
  return (
    <div className={`relative flex items-center justify-center overflow-hidden ${className}`} style={{ width, height }} aria-hidden="true">
      <StaticPage page={{ width: object.width, height: object.height, background: { type: 'none' }, objects: [{ ...object, id: object.id || 'preview', x: 0, y: 0, layer: 0 }] }} scale={k} showBackground={false} style={{ width: w, height: h, overflow: 'visible' }} />
    </div>
  );
};

/** Saved (cloud) block preview: thumbnail when available, otherwise a live render. */
export const UserBlockPreview = ({ block, width = 112, height = 76 }) => (
  block.thumbnail
    ? <div className="flex items-center justify-center overflow-hidden" style={{ width, height }}><img src={block.thumbnail} alt="" className="max-h-full max-w-full object-contain" /></div>
    : <BlockPreview object={{ ...block.object, x: 0, y: 0 }} width={width} height={height} />
);

/* ------------------------------------------------------------------ */
/* Calendar options                                                     */
/* ------------------------------------------------------------------ */

const YEARS = (() => { const y = new Date().getFullYear(); return Array.from({ length: 9 }, (_, i) => y - 2 + i); })();

/** Month / year / week-start controls shared by the dropdown and the library dialog. */
export const CalendarOptions = ({ value, onChange, compact = false }) => {
  const shift = (delta) => {
    const d = new Date(value.year, value.month + delta, 1);
    onChange({ year: d.getFullYear(), month: d.getMonth() });
  };
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? 'text-[12px]' : 'text-sm'}`} data-testid="calendar-options">
      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => shift(-1)} aria-label="Previous month" data-testid="calendar-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
      <Select value={String(value.month)} onValueChange={(v) => onChange({ month: Number(v) })}>
        <SelectTrigger className="h-7 w-[124px] text-[12px]" aria-label="Month" data-testid="calendar-month"><SelectValue /></SelectTrigger>
        <SelectContent className="z-[120]">{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i)} data-testid={`calendar-month-${i}`}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(value.year)} onValueChange={(v) => onChange({ year: Number(v) })}>
        <SelectTrigger className="h-7 w-[80px] text-[12px]" aria-label="Year" data-testid="calendar-year"><SelectValue /></SelectTrigger>
        <SelectContent className="z-[120]">{(YEARS.includes(value.year) ? YEARS : [value.year, ...YEARS]).map((y) => <SelectItem key={y} value={String(y)} data-testid={`calendar-year-${y}`}>{y}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => shift(1)} aria-label="Next month" data-testid="calendar-next-month"><ChevronRight className="h-4 w-4" /></Button>
      <Select value={String(value.weekStart)} onValueChange={(v) => onChange({ weekStart: Number(v) })}>
        <SelectTrigger className="h-7 w-[132px] text-[12px]" aria-label="Week starts on" data-testid="calendar-week-start"><SelectValue /></SelectTrigger>
        <SelectContent className="z-[120]">
          <SelectItem value="0" data-testid="calendar-week-start-0">Starts Sunday</SelectItem>
          <SelectItem value="1" data-testid="calendar-week-start-1">Starts Monday</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

/** Hook: calendar prefs as React state, persisted through calendarPrefs. */
export const useCalendarPrefs = () => {
  const [cal, setCal] = useState(getCalendarPrefs);
  const update = (next) => setCal(setCalendarPrefs(next));
  return [cal, update];
};

/* ------------------------------------------------------------------ */
/* Saved blocks (cloud)                                                 */
/* ------------------------------------------------------------------ */

export const useSavedBlocks = (category, reloadKey = 0) => {
  const [blocks, setBlocks] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  // Saving a block elsewhere (context menu / Add selection) refreshes every open gallery.
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('wp:building-blocks-changed', bump);
    return () => window.removeEventListener('wp:building-blocks-changed', bump);
  }, []);
  useEffect(() => {
    let alive = true;
    setError(null);
    listBuildingBlocks(category === USER_CATEGORY ? undefined : category).then((b) => alive && setBlocks(b)).catch(() => { if (alive) { setBlocks([]); setError('Saved blocks could not be loaded.'); } });
    return () => { alive = false; };
  }, [category, reloadKey, tick]);
  return { blocks, error, setBlocks };
};

/* ------------------------------------------------------------------ */
/* Ribbon dropdown gallery                                              */
/* ------------------------------------------------------------------ */

const tileClass = 'group/tile flex flex-col items-stretch rounded-md border border-border bg-background hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring p-1 text-left transition-colors duration-150';

/** Content of an Insert ▸ Building Blocks dropdown for one category. */
export const BuildingBlockDropdown = ({ category, onClose }) => {
  const { state, openDialog } = useEditor();
  const ins = useInsert();
  const [cal, setCal] = useCalendarPrefs();
  const isCalendar = category === 'Calendars';
  const { schemeId, pages, businessInfo } = state;
  const ctx = useMemo(() => blockContext({ schemeId, pages, businessInfo }, cal), [schemeId, pages, businessInfo, cal]);
  const starters = useMemo(() => blocksFor(category).map((b) => ({ block: b, object: buildBlockObject(b, ctx) })), [category, ctx]);
  const { blocks: saved, error } = useSavedBlocks(category);
  const groups = useMemo(() => {
    const m = new Map();
    starters.forEach((s) => { const g = s.block.group || 'General'; if (!m.has(g)) m.set(g, []); m.get(g).push(s); });
    return [...m.entries()];
  }, [starters]);
  const selectedCount = state.selectedObjectIds.length;

  const insertStarter = (block) => { ins.insertBuildingBlock(block); onClose && onClose(); };
  const insertSaved = (b) => { ins.insertUserBlock(b); onClose && onClose(); };

  return (
    <div className="w-[392px] max-w-[calc(100vw-24px)]" data-testid={`building-blocks-dropdown-${category.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-center justify-between px-1 pb-1">
        <div className="text-[12px] font-semibold">{category}</div>
        <div className="text-[11px] text-muted-foreground">{starters.length} starter{starters.length === 1 ? '' : 's'}{saved?.length ? ` · ${saved.length} saved` : ''}</div>
      </div>
      {isCalendar && <div className="px-1 pb-2"><CalendarOptions value={cal} onChange={setCal} compact /></div>}
      <div className="max-h-[320px] overflow-y-auto pr-1 space-y-2" data-testid="building-blocks-gallery">
        {groups.map(([groupName, items]) => (
          <div key={groupName}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-1 pb-1">{groupName}</div>
            <div className="grid grid-cols-3 gap-1.5 px-1">
              {items.map(({ block, object }) => (
                <button key={block.id} type="button" onClick={() => insertStarter(block)} className={tileClass} title={`Insert ${block.name}`} data-testid={`building-block-item-${block.id}`}>
                  <div className="h-[76px] w-full flex items-center justify-center rounded-sm bg-[repeating-conic-gradient(hsl(var(--muted))_0%_25%,transparent_0%_50%)] bg-[length:12px_12px] overflow-hidden">
                    <BlockPreview object={object} width={112} height={72} />
                  </div>
                  <div className="text-[11px] mt-1 truncate leading-tight">{block.name}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-1 pb-1 flex items-center gap-1"><BookmarkPlus className="h-3 w-3" /> My {category}</div>
          {saved === null && <div className="px-1 pb-1 text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading saved blocks…</div>}
          {error && <div className="px-1 pb-1 text-[11px] text-destructive" data-testid="building-blocks-saved-error">{error}</div>}
          {saved && !saved.length && !error && <div className="px-1 pb-1 text-[11px] text-muted-foreground" data-testid="building-blocks-saved-empty">Nothing saved here yet — select objects and use “Add Selection to {category} Gallery”.</div>}
          {saved && saved.length > 0 && (
            <div className="grid grid-cols-3 gap-1.5 px-1" data-testid="building-blocks-saved">
              {saved.map((b) => (
                <button key={b.id} type="button" onClick={() => insertSaved(b)} className={tileClass} title={`Insert ${b.name}`} data-testid={`building-block-item-${b.id}`}>
                  <div className="h-[76px] w-full flex items-center justify-center rounded-sm bg-white overflow-hidden"><UserBlockPreview block={b} width={112} height={72} /></div>
                  <div className="text-[11px] mt-1 truncate leading-tight">{b.name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-border mt-1 pt-1">
        <MenuItemRow icon={LayoutGrid} label={`More ${category}...`} onClick={() => { onClose && onClose(); openDialog('building-block-library', { category }); }} testId={`ribbon-menu-more-blocks-${category.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
        <MenuItemRow icon={BookmarkPlus} label={`Add Selection to ${category} Gallery...`} disabled={!selectedCount} onClick={() => { onClose && onClose(); openDialog('building-block', { objectIds: state.selectedObjectIds, category }); }} testId={`ribbon-menu-add-to-gallery-${category.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
        <MenuItemRow icon={Puzzle} label="Manage Building Blocks..." onClick={() => { onClose && onClose(); openDialog('building-block', { manage: true }); }} testId="ribbon-menu-manage-blocks" />
      </div>
    </div>
  );
};
